import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, Info, Plus } from 'lucide-react';
import { NO_PRODUCT_FILTER, QUEUE_PAGE_SIZES, REQUEST_TYPES, type QueueItem } from '@product-portal/shared';
import { buttonVariants } from '@/components/ui/button';
import { ProbableDuplicateBadge, TeamsConcernedBadge } from '@/components/portal/duplicate-badges';
import { ImpactTag } from '@/components/portal/impact-tag';
import { KpiCard, KpiGrid } from '@/components/portal/kpi-card';
import { PageTitle } from '@/components/portal/page-title';
import { Pill } from '@/components/portal/pill';
import { Pagination } from '@/components/portal/pagination';
import { ProductTag } from '@/components/portal/product-tag';
import { SearchField } from '@/components/portal/search-field';
import { SectionCard } from '@/components/portal/section-card';
import { SelectField } from '@/components/portal/select-field';
import { StatusPill } from '@/components/portal/status-pill';
import { TypeTag } from '@/components/portal/type-tag';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQueue } from '@/hooks/use-product';
import { formatDate, formatRelative, shortName } from '@/lib/format';
import { formatBusinessDays, NO_PRODUCT_LABEL, TYPE_LABELS } from '@/lib/labels';
import { cn } from '@/lib/utils';

/**
 * « Il y a N j » is in CALENDAR days; « hors délai » comes from the API's
 * business-day computation. The two units live side by side on purpose.
 */
function SubmittedCell({ item }: { item: QueueItem }) {
  const age = `Il y a ${item.ageInDays} j`;
  return (
    <>
      <span className="block whitespace-nowrap text-sm text-foreground">{formatDate(item.submittedAt)}</span>
      <span className={cn('mt-0.5 block whitespace-nowrap text-xs font-bold', item.delay.overdue ? 'text-overdue-foreground' : 'text-muted-foreground')}>
        {age}
        {item.delay.overdue ? ' · hors délai' : item.delay.suspended ? ' · délai suspendu' : ''}
      </span>
    </>
  );
}

const LAST_VISIT_KEY = 'product-portal.queue.last-visit';
const FIRST_VISIT_WINDOW_MS = 7 * 24 * 3600_000;

/**
 * « Nouveau »: a request still Soumise, submitted since this browser's previous
 * visit of the queue (a week back on a first visit). Kept in localStorage:
 * a per-viewer convenience, never shared, never read by the API.
 */
function usePreviousVisit(): number {
  const [previous] = useState(() => {
    try {
      const stored = Number(window.localStorage.getItem(LAST_VISIT_KEY));
      return stored > 0 ? stored : Date.now() - FIRST_VISIT_WINDOW_MS;
    } catch {
      return Date.now() - FIRST_VISIT_WINDOW_MS;
    }
  });
  useEffect(
    () => () => {
      try {
        window.localStorage.setItem(LAST_VISIT_KEY, String(Date.now()));
      } catch {
        // Private window or blocked storage: every visit is a first one.
      }
    },
    [],
  );
  return previous;
}

const STATUS_FILTERS = [
  { value: 'to_qualify', label: 'À qualifier' },
  { value: 'submitted', label: 'Soumise' },
  { value: 'qualifying', label: 'Qualification en cours' },
];

/** 07 · File de qualification. Overdue requests first (sorted by the API), then by age. Fits 1440px. */
export function QualificationQueuePage() {
  const [q, setQ] = useState('');
  const [productId, setProductId] = useState('');
  const [type, setType] = useState('');
  const [status, setStatus] = useState('to_qualify');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(6);
  const queue = useQueue({ q, productId, type, status, page, pageSize });
  const previousVisit = usePreviousVisit();
  const data = queue.data;
  const summary = data?.summary;
  const reset = <T,>(set: (v: T) => void) => (value: T) => {
    set(value);
    setPage(1);
  };

  return (
    <>
      <PageTitle title="File de qualification" subtitle="Demandes soumises par les équipes métier · engagement de qualification sous 2 jours ouvrés">
        {data?.lastJiraSyncAt && <p className="m-0 text-[13px] text-muted-foreground">Synchronisé avec Jira {formatRelative(data.lastJiraSyncAt).toLowerCase()}</p>}
        <Link to="/produit/saisie" className={cn(buttonVariants(), 'text-[13px] no-underline')}>
          <Plus aria-hidden="true" className="size-4" strokeWidth={2.2} />
          Saisir pour un collaborateur
        </Link>
      </PageTitle>

      <KpiGrid>
        <KpiCard label="À qualifier" value={summary?.toQualify ?? '—'} hint={summary ? `dont ${summary.toQualifyBugs} bug${summary.toQualifyBugs > 1 ? 's' : ''}` : undefined} />
        <KpiCard label="Hors délai (> 2 j ouvrés)" value={summary?.overdue ?? '—'} tone="danger" hint="À traiter en premier" />
        <KpiCard label="Qualification en cours" value={summary?.qualifying ?? '—'} tone="warning" hint="Question posée au demandeur ou analyse en cours" />
        <KpiCard
          label="Qualifiées cette semaine"
          value={summary?.qualifiedThisWeek ?? '—'}
          tone="info"
          hint={summary?.medianDelayThisWeek != null ? `Délai médian ${formatBusinessDays(summary.medianDelayThisWeek)}` : 'Délai médian —'}
        />
      </KpiGrid>

      <SectionCard
        title="Demandes entrantes"
        toolbar={
          <>
            <SearchField label="Rechercher" placeholder="Réf., titre, demandeur…" value={q} onChange={reset(setQ)} className="max-w-80" />
            <div className="ml-auto flex flex-wrap gap-2.5">
              <SelectField label="Produit" value={productId} onChange={(e) => reset(setProductId)(e.target.value)} className="min-w-40">
                <option value="">Tous les produits</option>
                {(data?.products ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
                {/* ADR-003: requests without product. */}
                <option value={NO_PRODUCT_FILTER}>{NO_PRODUCT_LABEL}</option>
              </SelectField>
              <SelectField label="Type" value={type} onChange={(e) => reset(setType)(e.target.value)} className="min-w-[130px]">
                <option value="">Tous</option>
                {REQUEST_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </SelectField>
              <SelectField label="Statut" value={status} onChange={(e) => reset(setStatus)(e.target.value)} className="min-w-[190px]">
                {STATUS_FILTERS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </SelectField>
            </div>
          </>
        }
        footer={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data?.total ?? 0}
            itemLabel="demandes"
            suffix="triées par ancienneté"
            onPageChange={setPage}
            pageSizeOptions={QUEUE_PAGE_SIZES}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            ariaLabel="Pagination de la file"
          />
        }
      >
        <Table minWidth={1100}>
          <TableHeader>
            <TableRow>
              <TableHead>Demande</TableHead>
              <TableHead>Produit</TableHead>
              <TableHead>Demandeur</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Impact déclaré</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Soumise le</TableHead>
              <TableHead>
                <span className="sr-only">Action</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.items ?? []).map((item) => {
              const open = item.status === 'submitted';
              const isNew = open && new Date(item.submittedAt).getTime() > previousVisit;
              return (
                <TableRow key={item.id} className={cn(item.delay.overdue && 'bg-overdue')}>
                  <TableCell>
                    <Link to={`/produit/demandes/${item.id}`} className="font-bold text-foreground no-underline hover:text-foreground">
                      {item.title}
                    </Link>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{item.reference}</span>
                    {(isNew || item.probableDuplicate || item.teamsConcerned > 1) && (
                      <span className="mt-1.5 flex flex-wrap gap-1.5">
                        {isNew && <Pill className="bg-primary text-primary-foreground">Nouveau</Pill>}
                        {item.probableDuplicate && <ProbableDuplicateBadge reference={item.probableDuplicate.reference} />}
                        <TeamsConcernedBadge count={item.teamsConcerned} />
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <ProductTag name={item.product?.name ?? NO_PRODUCT_LABEL} />
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <span className="block font-bold">{shortName(item.requester)}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{item.teamName}</span>
                  </TableCell>
                  <TableCell>
                    <TypeTag type={item.type} />
                  </TableCell>
                  <TableCell>
                    <ImpactTag impact={item.impactDeclared} />
                  </TableCell>
                  <TableCell>
                    <StatusPill status={item.status} />
                  </TableCell>
                  <TableCell>
                    <SubmittedCell item={item} />
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/produit/demandes/${item.id}`}
                      className={cn(buttonVariants({ variant: open ? 'default' : 'outline', size: 'sm' }), 'min-h-9 no-underline')}
                    >
                      {open ? 'Qualifier' : 'Ouvrir'}
                      <ArrowRight aria-hidden="true" className="size-4" strokeWidth={2.2} />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
            {data && data.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Aucune demande à qualifier.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionCard>

      <div className="flex items-start gap-3 rounded-card border border-border bg-card px-[18px] py-3.5 text-[13px] leading-normal text-foreground-secondary">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
        <span>
          Le délai de 2 jours ouvrés est suspendu tant qu'une question posée pendant la qualification attend la réponse du demandeur. Les lignes en rouge ont
          dépassé l'engagement et remontent en tête de file.
        </span>
      </div>
    </>
  );
}
